

  
const content = `# OnData Encryption Tool

The OnData Encryption Tool has three operating modes:

- Encryption (default operating mode)
- Decryption
- ApplyEncryptionSettings

The **Encryption** operating mode is used to encrypt a database according to the encryption and obfuscation rules configured at the OnData Portal, or the obfuscation rules configured in a local configuration file.

- Encryption: Encrypt sensitive data such that the resulting encrypted values have the same format (data type) as that of the input values. The encrypted values can be decrypted with the correct encryption key.
- Obfuscation: If an obfuscation rule is specified for a column either at the OnData Portal, or in a local configuration file, the OnData Encryption Tool can obfuscate the column data based on the obfuscation rule. 
The OnData Encryption Tool will randomly pick a value from a list of values specified in the obfuscation rule. The list of values should meet the data validation rules or constraints in the database or related applications.
The OnData Portal supports four types of obfuscation rules:
  - Table Column: Randomly pick a value from a related lookup table.
  - Value List: Randomly pick a value from a list of specific values.
  - Value Range: Randomly pick a value from a continuous range of values.
  - Clear Data: Clear the existing data in a column.

**Note**: Unlike encrypted values, the obfuscated values cannot be decrypted to the original values. Therefore, you should not use the obfuscation feature directly on a database in your production environment. 
Instead, you may restore a backup of your production database on another database server and run the OnData Encryption Tool against the restored database instance.

The **Decryption** operating mode is used to decrypt an encrypted database. All obfuscation rules will be ignored.

The **ApplyEncryptionSettings** operating mode is used to apply encryption setting changes. For more information about **ApplyEncryptionSettings** operating mode, please refer to the [**Apply Encryption Setting Changes**](reference_doc#how-to_guides/drivers/apply_encryption_setting_changes)

## 1. Prerequisites

Here are the requirements for using the OnData Encryption Tool.

### 1.1 Operating System

The OnData Encryption Tool comes with two packages, with one supporting 64-bit Windows operating systems and the other supporting 64-bit Linux operating systems.

- Windows x64
- Linux x64

### 1.2 Database User Permissions

OnData Encryption Tool requires a database user with the following permissions assigned at a minimum:

- SQL Server
  - db_datareader
  - db_datawriter
  - db_ddladmin
  - VIEW SERVER STATE

- Oracle
  - SELECT_CATALOG_ROLE
  - ALTER ANY SEQUENCE
  - LOCK ANY TABLE
  - UPDATE ANY TABLE
  - INSERT ANY TABLE
  - SELECT ANY DICTIONARY
  - SELECT ANY TABLE
  - ALTER ANY TABLE
  - ALTER ANY TRIGGER
  - CREATE SESSION
  - DROP ANY TABLE
  - ALTER ANY MATERIALIZED VIEW
  - CREATE ANY TABLE
  - CREATE ANY INDEX
  - ALTER ANY INDEX
  - DROP ANY INDEX

### 1.3 Enable TCP/IP Connections

- SQL Server: The TCP/IP network protocol needs to be enabled for the database server. You can confirm such configuration with the \`SQL Server Configuration Manager\`. Please refer to [this link](https://docs.microsoft.com/en-us/sql/database-engine/configure-windows/enable-or-disable-a-server-network-protocol?view=sql-server-ver15) for more details.

### 1.4 File Access Permissions

The OnData Encryption Tool must have read and write permissions to the folder where the OnData Encryption Tool is located.

### 1.5 Additional Configuration for MySQL or MariaDB

For MySQL or MariaDB databases, we need to check the local_infile with this query: \`SHOW GLOBAL VARIABLES LIKE 'local_infile';\`

- If the value of the local_infile is set to ON, you don't need to do anything else.
- If the value of the local_infile is set to OFF, it means that data import from the client side is disabled. Please update the my.cnf file (for Mac and Linux operating systems) or the my.ini file (for Windows operating systems) with the following configuration. Once the configuration file is updated, save it and restart your MySQL/MariaDB server.

\`\`\`ini
[mysqld]
local_infile=ON
\`\`\`

Please refer to [this link](https://dev.mysql.com/doc/refman/8.0/en/load-data-local-security.html#load-data-local-configuration) for more details.

### 1.6 Parallelism

- Oracle

  - The OnData Encryption Tool can fully utilize the parallel processing capability of the database server. If the database server has many CPU cores, you may review 
  and update the processes parameter.
  
  - To view the current processes parameter, use the command: \`show parameter processes\`. If the parameter is low, you may increase it to a higher value, 
  for example: \`alter system set processes=2000 scope=spfile\`

### 1.7 Backup Database

It is highly recommended to back up your database before running the OnData Encryption Tool, to prevent any potential data loss due to incorrect configuration or other issues during encryption. If you already have a complete backup of your database, you do not have
to do another backup.

## 2. Configuration

### 2.1 \`appsettings.ini\`

- \`DatabaseType\`: Indicates the engine type of the database to be processed. Please use one of these values:
  - MySQL: 1
  - PostgreSQL: 2
  - SQL Server: 3
  - MariaDB: 5
  - Oracle: 6
  - DB2: 7

- \`ServerAddress\`: The server IP address or Fully Qualified Domain Name (FQDN) where the database is hosted.

- \`Port\`: The server port that the database uses for connectivity. The default ports are:
  - MySQL: 3306
  - PostgreSQL: 5432
  - SQL Server: 1433
  - MariaDB: 3306
  - Oracle: 1521
  - DB2: 25000

- \`DatabaseName\`: The name of the database that you want to process. It should match exactly with the database name on your database server and the database name you created at the OnData Portal. 

- \`Username\`: The name of the database user account who has sufficient permissions to read and write the database.

  **Note**: \`Username\` is not required when database type is SQL Server and \`SqlServerIntegratedSecurity\` is set to true.

- \`Password\`: The password of the database user account who has sufficient permission to read and write the database. For more information, please see **How to run** section below.
  - If a password is specified on the command line with the "-p {password}" option, that password will be used.
  - If such option does not exist in the command line, the password specified below will be used.
  - If the password is not specified here either, you will be prompted to enter the password when running the OnData Encryption Tool.

  **Note**: \`Password\` is not required when database type is SQL Server and \`SqlServerIntegratedSecurity\` is set to true.

- \`SqlServerIntegratedSecurity\`: When the database type is MS SQL Server, you may choose to use integrated authentication with the current Windows account by setting SqlServerIntegratedSecurity to true. 
Otherwise please specify the username and password above and set SqlServerIntegratedSecurity to false.
  
  **Note**: \`Username\` and \`Password\` are not required when database type is SQL Server and \`SqlServerIntegratedSecurity\` is set to true.

- \`ApplyObfuscationRules\`: Whether to apply obfuscation rules. Note: **It can only be set to true in the Encryption operating mode to process a non-production database instance. DO NOT apply obfuscation on a production database instance**.
  - If it is set to false, only encryption rules will be applied. All obfuscation rules will be ignored, and no obfuscation will be performed.
  - If it is set to true:
    - If both encryption and obfuscation rules are configured for a column, only the obfuscation rule will be applied.
    - If only encryption rule is configured for a column, the encryption rule is applied.
    - If only obfuscation rule is configured for a column, the obfuscation rule is applied.

- \`LogErrorData\`: Whether to log the original data of columns that failed on encryption. If you do not want any sensitive data in the logs, you should always set this option to false.

- \`SubsettingEnabled\`: Whether to enable data subsetting. If this is set to true, you need to configure the subsetting details at the OnData Portal.

- \`DatabaseLinkName\`: The database link name is required for Oracle data subsetting.

- \`SourceDbName\`: If subsetting is enabled, you need to specify the name of source database that is used for subsetting. The resulting database that contains a subset of records from the source database is specified by the DatabaseName above.

- \`OperatingEnvironment\`: Whether we are processing a database in a production environment or non-production environment. The default value is 0 (NonProduction). For more information, please refer to the [**Apply Encryption Setting Changes**](reference_doc#how-to_guides/drivers/apply_encryption_setting_changes)
  - 0 (NonProduction)
  - 1 (Production)

- \`OperatingMode\`: Whether we are encrypting or decrypting a database, or applying encryption setting changes to a production database. The default value is 0 (Encryption mode).
  - 0 (Encryption): Encrypt data according to the current encryption mappings
  - 1 (Decryption): Decrypt data according to the stored snapshot of encryption mappings that were originally used to encrypt the database
  - 2 (ApplyEncryptionSettings): Apply encryption setting changes. Based on the type of changes, the OnData Encryption Tool will act accordingly. For example:
    - If the encryption rule of an encrypted column is removed, the encrypted data will be decrypted.
    - If an encryption rule is added to a column, the column data will be encrypted.
    - If the encryption rule for an encrypted column has changed, the encrypted data will be decrypted first, and then encrypted with the new rule.
    - If the encryption key for a database has changed, the encrypted data will be decrypted first, and then encrypted by the new encryption key.

- \`ThreadingFactor\`: The number of threads to use per logical processor. This configuration may be used to optimize the processing performance. It typically should be set between 1 and 2. 
It must be a value between 0 and 2 but cannot be 0. The default value is 1. The default value should be used in most cases.

- \`MaxDegreeOfParallelism\`: The maximum number of concurrent tasks to be enabled. This configuration may be used to optimize the processing performance. 
The default value is 64. The default value should be used in most cases.

- \`PageSize\`: The number of rows to process for each query task. This configuration may be used to optimize the processing performance. 
The default value is 1,000,000. It must be between 100,000 and 10,000,000. The default value should be used in most cases.

- \`ProxyHost\`: If a network proxy is used, please specify the proxy IP address or Fully Qualified Domain Name (FQDN).

- \`ProxyPort\`: If a network proxy is used, please specify the port number of the network proxy.

- \`ProxyAuthType\`: If a network proxy is used, please specify the authentication type of network proxy. Please use one of these values: NoAuth -> 0, BasicAuth -> 1, DigestAuth -> 2, BearerTokenAuth -> 3

- \`ProxyUsername\`: If a network proxy is used, please specify the username for proxy authentication.

- \`ProxyPassword\`: If a network proxy is used, please specify the password for proxy authentication. If ProxyAuthType is set to 3 (BearerTokenAuth), please specify the token in the ProxyPassword field.

### 2.2 \`ObfuscateConfig.json\`

This is the configuration file for data obfuscation purpose. The data obfuscation rules can be configured at the OnData Portal directly. In case you prefer to 
use this configuration file instead of the OnData Portal, you can update this file with your obfuscation settings. When this configuration file is used, 
the data obfuscation rules configured at the OnData Portal will be ignored. If you do need to use the data obfuscation feature, or you would like to use the data 
obfuscation rules configured at the OnData Portal, please delete this file.  
  
Below is an example of the configuration file:

\`\`\`json
[
  {
    "Schema": "HumanResources",
    "Table": "Employee",
    "Column": "Gender",
    "AllowedValues": [
      "M",
      "F"
    ]
  },
  {
    "Schema": "Production",
    "Table": "Document",
    "Column": "Status",
    "AllowedValues": [
      "1",
      "2",
      "3"
    ]
  }
]
\`\`\`

This configuration file contains an array of columns to be obfuscated. For each column, configure the following four properties:

- \`Schema\`: Schema name.
- \`Table\`: Table name.
- \`Column\`: Column name.
- \`AllowedValues\`: List of allowed values.
  - **Please always use string values here even if the data type of the column is numeric**. The OnData Encryption Tool will convert them to the right data type when the data is imported into the database;
  - AllowedValues should contain at least one value;
  - AllowedValues should not contain duplicated values;
  - Because the configuration file is in JSON format, if the value contains \`&bsol;\` or \`"\`, please replace with \`&bsol;&bsol;\` and \`&bsol;"\`;
  - If the value needs to contain a control character or non-printable character, you may use its Unicode value. For example, if you want the value to contain an LF(line feed), you can use either \`&bsol;n\` or \`&bsol;u000A\`;
  - Do not use the \`N\` prefix of SQL Server.

**Important Notes**:

- \`Schema\`, \`Table\` and \`Column\` are case-insensitive for SQL Server, but are case-sensitive for other database engines;
- The columns configured in the \`ObfuscateConfig.json\` file must exist in the database;
- Always use string values for the list of \`AllowedValues\`, even if the data type of that column is numeric. The OnData Encryption Tool will convert them to the right data type when the data is imported into the database.

## 3. How to Run

Before you run the OnData Encryption Tool, you need to register it as an OnData client. For more information about how to register an OnData client, please refer to the [**OnData Client Registration**](reference_doc#how-to_guides/drivers/client_registration).  

You can run the OnData Encryption Tool from different Shell environments. Below is the list for Windows and Linux:

- Windows
  - Command line
  - PowerShell

- Linux
  - Bash

Command examples:

- Windows: \`.&bsol;OnDataEncryptionTool.exe -p '<password>'\`
- Linux: \`./OnDataEncryptionTool -p '<password>'\`

Note: If you choose to place the password in a pair of single quotation marks, and the password itself contains single quotation marks, you have to replace \`'\` with \`''\` (two single quotation marks).

The OnData Encryption Tool will look for the password of the database user in the following ways sequentially:

- If a password is specified on the command line with the \`-p '<password>'\` option, the password from the command line will be used.
- If such option does not exist in the command line, the OnData Encryption Tool will search the appsettings.ini file for the password.
- If a password does not exist in the appsettings.ini file either, the OnData Encryption Tool will prompt you to enter the password for the database user.

  **Note**: \`Password\` is not required when database type is SQL Server and \`SqlServerIntegratedSecurity\` is set to true.

## 4. Limitations

- Some database constraints may have to be removed. To allow for the randomness of encrypted results, some of the database constraints may have to be removed. For example, it makes sense to have a database constraint to limit the birthdates to dates in the past. But the encryption engine may generate a date in the future. Such constraint may need to be removed before encryption can be applied.

### 4.1 Limitations for SQL Server

- Currently the system-versioned temporal tables of SQL Server cannot be processed.
- Data subsetting is not available for graph tables.

### 4.2 Limitations for Oracle

- The object table, external table, queue table, read-only table and materialized view cannot be processed.
- Data subsetting is not available for read only tables, so alter the tables to 'read write' manually if you need to do data subsetting for them. Otherwise, the read only tables will be ignored.
- For referenced partition tables, the partition key columns cannot be encrypted even if the table row movement is enabled. For other partitioned tables, The partition key columns cannot be encrypted if the table row movement is not enabled.
`
  
export default content;
